import apiService from './api.service';
import { ExportOptions, ExportStats } from '../types/export.types';
import { supabase } from '../config/supabase.config';

class ExportService {
  /**
   * Export leads to CSV or JSON
   */
  async exportLeads(options: ExportOptions): Promise<{ blob: Blob; filename: string }> {
    try {
      const params = new URLSearchParams();
      params.append('format', options.format);

      if (options.filters) {
        const { status, priority, assignedTo, dateFrom, dateTo, search } = options.filters;

        if (status && status.length > 0) {
          params.append('status', status.join(','));
        }
        if (priority && priority.length > 0) {
          params.append('priority', priority.join(','));
        }
        if (assignedTo && assignedTo.length > 0) {
          params.append('assignedTo', assignedTo.join(','));
        }
        if (dateFrom) {
          params.append('dateFrom', dateFrom);
        }
        if (dateTo) {
          params.append('dateTo', dateTo);
        }
        if (search) {
          params.append('search', search);
        }
      }

      // Get token from Supabase session
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;

      console.log('🔐 Export token:', token ? 'Found' : 'NOT FOUND');

      if (!token) {
        throw new Error('No authentication token found. Please log in again.');
      }

      const response = await fetch(`${apiService.defaults.baseURL}/export/leads?${params.toString()}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to export leads');
      }

      // Get filename from Content-Disposition header
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `leads_export_${new Date().toISOString().split('T')[0]}.${options.format}`;
      
      if (contentDisposition) {
        const matches = /filename="([^"]+)"/.exec(contentDisposition);
        if (matches && matches[1]) {
          filename = matches[1];
        }
      }

      const blob = await response.blob();

      return { blob, filename };
    } catch (error) {
      console.error('Export leads error:', error);
      throw error;
    }
  }

  /**
   * Get export statistics
   */
  async getExportStats(): Promise<ExportStats> {
    try {
      console.log('📊 Fetching export stats from API...');
      const response = await apiService.get<ExportStats>('/export/stats');
      console.log('📊 Raw API response:', response);
      console.log('📊 Response ', response.data);
      
      // Axios returns data in response.data
      const stats = response.data;
      console.log('📊 Parsed stats:', stats);
      
      // Validate the response structure
      if (!stats || typeof stats.totalLeads === 'undefined') {
        console.error('❌ Invalid stats structure:', stats);
        throw new Error('Invalid export statistics format received from server');
      }
      
      return stats;
    } catch (error: any) {
      console.error('❌ Get export stats error:', error);
      console.error('❌ Error response:', error.response?.data);
      console.error('❌ Error status:', error.response?.status);
      throw error;
    }
  }

  /**
   * Download file helper
   */
  downloadFile(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }
}

export const exportService = new ExportService();